/**
 * Renders trusted HTML stored in plain-text product metafields.
 * Liquid escapes HTML in multi-line text fields, so we parse it client-side.
 *
 * Follow the metafield as written:
 *   <br>  → next line
 *   space → space
 * Do not invent extra <br> tags from source newlines around existing breaks.
 */
class MetafieldHtmlContent extends HTMLElement {
  connectedCallback() {
    this.parse();
  }

  /**
   * @param {string} [raw]
   */
  parse(raw) {
    // ASTERI PATCH (Shopify): do not re-parse on reconnect / variant swap.
    // textContent strips <strong> and other tags; overwriting innerHTML from it
    // dropped markup that Liquid already put in the DOM. Skip when already parsed
    // unless a new raw string is passed (JSON overlay).
    if (raw == null && this.dataset.parsed === 'true') return;

    const source = raw == null ? this.textContent ?? '' : String(raw);
    const html = source.replace(/^[\s\u00a0]+|[\s\u00a0]+$/g, '');
    const hasLiveHtml = raw == null && this.children.length > 0;

    if (!html) {
      if (hasLiveHtml) {
        this.dataset.parsed = 'true';
        return;
      }
      this.textContent = '';
      this.dataset.parsed = 'true';
      return;
    }

    if (!html.includes('<')) {
      // ASTERI PATCH (Shopify): textContent has no "<" when <strong> is already
      // a real node. Leave that HTML alone. Only convert true plain-text metafields.
      if (hasLiveHtml) {
        this.dataset.parsed = 'true';
        return;
      }
      this.innerHTML = this.#plainTextToHtml(html);
      this.dataset.parsed = 'true';
      return;
    }

    let parsed = this.#followHtml(html);

    if (parsed.includes('<li>') && !parsed.includes('<ul') && !parsed.includes('<ol')) {
      parsed = `<ul>${parsed}</ul>`;
    }

    this.innerHTML = parsed;
    this.#trimLeadingText();
    this.dataset.parsed = 'true';
  }

  /** Plain text: one newline = next line; extra blank lines stay as space. Spaces are kept. */
  #plainTextToHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\r\n/g, '\n')
      .replace(/^\n+/, '')
      .replace(/\n+$/, '')
      .replace(/\n/g, '<br>');
  }

  /**
   * Keep metafield <br> tags as line breaks.
   * Drop newlines sitting next to <br> (editor formatting) so they do not add extra gaps.
   * Leave real space characters in the copy untouched.
   */
  #followHtml(html) {
    return String(html)
      .replace(/\r\n/g, '\n')
      .replace(/(<br\s*\/?>)[\t\n\f\r]+/gi, '$1')
      .replace(/[\t\n\f\r]+(<br\s*\/?>)/gi, '$1');
  }

  #trimLeadingText() {
    const first = this.firstChild;
    if (first?.nodeType === Node.TEXT_NODE) {
      first.textContent = first.textContent.replace(/^[\n\r\t\f\u00a0]+/, '');
      if (!first.textContent) first.remove();
    }
  }
}

if (!customElements.get('metafield-html-content')) {
  customElements.define('metafield-html-content', MetafieldHtmlContent);
}
